// Art-review gallery (`?gallery=1`): seeded boards from the forge's layout generator
// (`generate_board` in fk-sim-wasm) drawn through the view-tile ruleset, no server needed.
// Query: `style=dungeon|cavern|bog|forest`, `seed=N`, `kit=<key>` (overrides the generator's
// pick), `w`/`h`, `zoom=2|3|4`, `marks=0` to hide the vector exit/objective marks; lighting
// (docs/19): `hour=0..24` (omit for the seed's hour), `light=full|soft|off`, `units=1` stands a
// small party and a goblin band on open ground to judge legibility; weather (docs/20):
// `weather=clear|overcast|rain|storm|fog|snow|heat|sandstorm|blizzard|frost|sleet[:intensity[:wind]]`
// (omit for the seed's weather); seasons (docs/23): `season=spring|summer|autumn|winter` (omit for none);
// fog of war (docs/31): `fog=clear|night|underground|weather` (comma-stack, e.g. `night,weather`) draws
// a GALLERY-ONLY mock FogView (fog/mock.ts — a plausible ray cast, not the rules) over a party, `fogstep=N`
// walks the party N squares east, `focus=<hero name>` shows one hero's sight; wildlife (docs/35): `wild=1`
// keeps a room of creatures in wasm (the module's own tick, on the device clock; GALLERY-ONLY).
// Commissioned terrain (docs/186): `biome=<biome>` and `time=day|night` choose the set with the kit, `season` and
// the hour; `oryx=1` keeps the Oryx look. `window.__fkTerrain()` reports the set and the standing images.
// docs/186 EA13–EA14: `look=<json>` lays the forge's rooms' floors and rails on the board (`encounter_stage.look`:
// `{"names":["floor_flags","runner"],"floors":"0*40,1*12,…","rails":"…"}`), `country=<kit>` names a place's country;
// `place=<kit>` stages the fight (`fight=`, `ground=`, `heroes=`, `seed`) in a place kit with its own look and country.
// Wheel or pinch zooms toward the pointer, a drag pans, 0 fits the board again.
// WASD or the arrows pan, Q / E zoom out / in.
// Keys: N / P next / previous seed, C cycles style, K cycles kit, M toggles marks, T / Y hour
// forward / back, L cycles the lighting mode, U toggles the units, X cycles the weather, Z the season,
// F the fog's veil, G walks the party a square east under the fog, B the wildlife, V walks Jo two squares
// east among the creatures (they react as they would to a resolved pulse).
// Fight maps (docs/66 RM12): `fight=<setup>` (Clash, Defend, Escort, Ambushed, Assault, Rescue, Raid,
// Hunt, Crossing) stages a fight as the forge does (`stage_fight`), with `recipe=<key>` (omit for
// one drawn as a fight would), `ground=<Ground>`, `heroes=N` and `seed`; a picker sits over the
// canvas, and the plan is drawn over the board: the heroes' zone (blue) and spots, each foe group
// (red) and the leader, the ward (green), the landmark (gold), the thing (cyan), every entry
// (ember, with the name the log gives it) and the exits. `zones=0` hides the overlay.
import Phaser from 'phaser';
import type { GameBoot } from '../Game';
import { TileRenderer } from '../board/TileRenderer';
import { VoidLayer } from '../board/VoidLayer';
import { fitZoom, centreBoard, pixelRatio, wheelZoomFactor, ZOOM_KEY_RATE } from '../board/BoardCamera';
import { boardFromWasmObj } from '../../core/sim/adapt';
import { wasmModule } from '../../core/sim/wasm';
import { KITS, oryxKeyOf } from '../tiling';
import { LightingLayer, climateFor, encounterHour, formatHour, type LightingMode } from '../lighting';
import { SEASONS, WEATHER_KINDS, encounterWeather, parseSeason, parseWeather, seasonLabel, weatherLabel, type Season, type Weather } from '../weather';
import { registerKitTextures } from '../board/heroTextures';
import { UnitSprite } from '../board/UnitSprite';
import { squareTarget, unitTarget, type SeeTarget } from '../encounter/seeThrough';
import type { FogView, Snapshot, UnitState } from '../../core/sim/types';
import { decodeLook } from '../../core/encounter/look';
import { FogLayer, mockFog, parseFogVeils } from '../fog';
import { PLAYER_COLOURS } from '../board/players';
import { WildlifeLayer, WildPreviewRoom, habitatOf, roomOf, type PreviewBody } from '../wildlife';
import { deviceNow } from '../../core/time';
import type { WildRow } from '../../core/wildlife/motion';
import { GROUNDS, PARTIES, SETUPS, fightStageFromWasm, recipesFor, sideOf, type FightStage } from './fightStage';
import { MatterLayer } from '../matter/MatterLayer';
import { objectTable } from '../matter/objectArt';
import { showroom } from '../matter/showroom';
import { thingShape } from '../../core/matter/shapes';
import type { TileData } from '../../core/sim/types';

const STYLES = ['dungeon', 'cavern', 'bog', 'forest'] as const;
/** What the vector marks mean (TileRenderer.drawProps); the HUD shows the same line. */
export const MARK_LEGEND = 'marks: « gold = exit (party enters/leaves) · ○ gold = objective';

export interface GalleryState { style: string; seed: number; kit?: string; w: number; h: number; zoom?: number; marks: boolean; hour?: number; light: LightingMode; units: boolean; weather?: string; season?: Season; fog?: string; fogStep: number; focus?: string; wild: boolean; shape: boolean;
  /** docs/66: a fight's setup to stage, with its recipe (empty = drawn), ground and party size. */
  fight?: string; recipe?: string; ground: string; heroes: number; zones: boolean;
  /** docs/186: the World's biome and a forced time of day for the commissioned art. */
  biome?: string; time?: 'day' | 'night';
  /** docs/186 EA13–EA14: the stage's `look` (raw JSON) and an outdoor place's country kit. */
  look?: string; country?: string;
  /** docs/187 PK2: stage the fight in a place kit (`stage_place`): town, docks, courtyard, graveyard, ruins, house, ship, barrow, mine, ember. */
  place?: string;
  /** docs/189 EO: `things=1` lays every thing of the objects atlas on one board (`showroom.ts`). */
  things: boolean }

const LIGHT_MODES: readonly LightingMode[] = ['full', 'soft', 'off'];

export function galleryStateFromUrl(): GalleryState {
  const q = new URLSearchParams(window.location.search);
  const n = (k: string, d: number): number => { const v = Number(q.get(k)); return Number.isFinite(v) && q.get(k) !== null ? v : d; };
  const zoom = n('zoom', 0);
  const light = q.get('light') as LightingMode | null;
  return {
    style: q.get('style') ?? 'dungeon', seed: n('seed', 1), kit: q.get('kit') ?? undefined, w: n('w', 18), h: n('h', 12), zoom: zoom || undefined, marks: q.get('marks') !== '0',
    hour: q.get('hour') !== null ? n('hour', 12) : undefined, light: light && LIGHT_MODES.includes(light) ? light : 'full', units: q.get('units') === '1',
    weather: parseWeather(q.get('weather')) ? q.get('weather')! : undefined,
    season: parseSeason(q.get('season')),
    fog: q.get('fog') ?? undefined, fogStep: n('fogstep', 0), focus: q.get('focus') ?? undefined, wild: q.get('wild') === '1', shape: q.get('shape') === '1',
    fight: q.get('fight') ?? (q.get('place') ? 'Clash' : undefined), recipe: q.get('recipe') ?? undefined, ground: q.get('ground') ?? 'Greenwood', heroes: n('heroes', 4), zones: q.get('zones') !== '0',
    biome: q.get('biome') ?? undefined, time: q.get('time') === 'day' || q.get('time') === 'night' ? (q.get('time') as 'day' | 'night') : undefined,
    look: q.get('look') ?? undefined, country: q.get('country') ?? undefined, place: q.get('place') ?? undefined, things: q.get('things') === '1',
  };
}

export class Gallery extends Phaser.Scene {
  private tiles!: TileRenderer;
  private edge!: VoidLayer;
  private state!: GalleryState;
  private caption!: Phaser.GameObjects.Text;
  private legend!: Phaser.GameObjects.Text;
  private board?: Snapshot['board'];
  private lighting!: LightingLayer;
  private unitLayer!: Phaser.GameObjects.Layer;
  private sprites: UnitSprite[] = [];
  /** docs/186 EA12: the square under the mouse. */
  private seeHover?: { x: number; y: number };
  private fogLayer!: FogLayer;
  private fogView?: FogView;
  private wild!: WildlifeLayer;
  private preview?: WildPreviewRoom;
  /** Review: scripted creatures (times relative to when they were set) instead of the preview's. */
  private script?: WildRow[];
  private scriptAt = 0;
  /** Review: the scripted creatures' clock held at this many ms after they were set. */
  private heldAt?: number;
  /** docs/66: the staged fight on show, its plan overlay and the picker. */
  private stage?: FightStage;
  private plan?: Phaser.GameObjects.Graphics;
  private planLabels: Phaser.GameObjects.Text[] = [];
  private propsLayer!: Phaser.GameObjects.Layer;
  private picker?: HTMLDivElement;
  /** docs/189: the things on show (`things=1`). */
  private thingFx?: MatterLayer;
  private matterLayers!: { low: Phaser.GameObjects.Layer; props: Phaser.GameObjects.Layer; high: Phaser.GameObjects.Layer; effects: Phaser.GameObjects.Layer };
  private thingLabels: Phaser.GameObjects.Text[] = [];

  /** The viewer has zoomed or dragged: keep their view across redraws until 0 fits the board again. */
  private looked = false;
  private held?: Record<string, Phaser.Input.Keyboard.Key>;

  constructor() { super('Gallery'); }

  create(): void {
    // docs/145 KH6: heroes in their kit looks (swapped on as each look bakes)
    const off = registerKitTextures(this, () => { for (const sp of this.sprites) sp.useKit(boot.manifest); });
    this.events.once('shutdown', off);
    const boot = this.registry.get('boot') as GameBoot;
    this.state = galleryStateFromUrl();
    this.cameras.main.setOrigin(0, 0).setRoundPixels(true);
    const terrain = this.add.layer(), light = this.add.layer(), lit = this.add.layer(), props = this.add.layer();
    const shadows = this.add.layer(), low = this.add.layer(), fog = this.add.layer(), edge = this.add.layer(), units = this.add.layer(), high = this.add.layer();
    this.unitLayer = units;
    this.propsLayer = props;
    this.matterLayers = { low, props, high, effects: this.add.layer() };
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { this.picker?.remove(); this.picker = undefined; });
    this.fogLayer = new FogLayer(this, fog, boot.manifest.cell, boot.manifest);
    this.tiles = new TileRenderer(this, terrain, props, boot.manifest, {
      lit, units,
      want: () => ({ ...(this.state.biome ? { biome: this.state.biome } : {}), ...(this.state.season ? { season: this.state.season } : {}), hour: this.hour(), ...(this.state.time ? { time: this.state.time } : {}) }),
      onReady: () => { if (this.scene.isActive()) this.render(); },
    });
    this.edge = new VoidLayer(this, edge, boot.manifest.cell);
    this.lighting = new LightingLayer(this, { light, shadows, low, high }, this.cameras.main, boot.manifest.cell);
    this.tiles.art?.setLighting(this.lighting);
    this.wild = new WildlifeLayer(this, units, shadows, boot.manifest.cell, { light: (x, y) => this.lighting.lightAt(x, y), sees: (x, y) => this.fogLayer.sees(x, y), sunny: () => this.lighting.sunny });
    // docs/33 E4: the canvas is in device pixels; the captions are laid out in CSS px
    const dpr = pixelRatio();
    const ui = this.cameras.add(0, 0, this.scale.width, this.scale.height, false, 'ui').setOrigin(0, 0).setZoom(dpr);
    ui.ignore([terrain, light, lit, props, shadows, low, fog, edge, units, high, this.matterLayers.effects]);
    this.caption = this.add.text(8, this.scale.height / dpr - 22, '', { fontFamily: 'monospace', fontSize: '13px', color: '#e8e2d0', backgroundColor: '#0b1020cc', padding: { x: 6, y: 3 } });
    this.legend = this.add.text(8, 8, MARK_LEGEND, { fontFamily: 'monospace', fontSize: '13px', color: '#e8e2d0', backgroundColor: '#0b1020cc', padding: { x: 6, y: 3 } });
    this.cameras.main.ignore([this.caption, this.legend]);
    this.input.keyboard?.on('keydown', (ev: KeyboardEvent) => {
      const k = ev.key.toLowerCase();
      // docs/66: in a fight, C cycles the setup, R the recipe, G the ground, H the party
      if (this.state.fight && 'crgh'.includes(k)) { this.cycleFight(k); this.render(); return; }
      if (k === 'n') this.state.seed += 1;
      else if (k === 'p') this.state.seed = Math.max(0, this.state.seed - 1);
      else if (k === 'c') this.state.style = STYLES[(STYLES.indexOf(this.state.style as typeof STYLES[number]) + 1) % STYLES.length]!;
      else if (k === 'k') { const keys = Object.keys(KITS); this.state.kit = keys[(keys.indexOf(this.state.kit ?? '') + 1) % keys.length]; }
      else if (k === 'm') this.state.marks = !this.state.marks;
      else if (k === 't' || k === 'y') this.state.hour = (((this.hour() + (k === 't' ? 1.5 : -1.5)) % 24) + 24) % 24;
      else if (k === 'l') this.state.light = LIGHT_MODES[(LIGHT_MODES.indexOf(this.state.light) + 1) % LIGHT_MODES.length]!;
      else if (k === 'u') this.state.units = !this.state.units;
      else if (k === 'z') { const i = this.state.season ? SEASONS.indexOf(this.state.season) : -1; this.state.season = i + 1 < SEASONS.length ? SEASONS[i + 1] : undefined; }
      else if (k === 'f') { const cyc = [undefined, 'clear', 'night', 'underground', 'weather', 'night,weather']; this.state.fog = cyc[(cyc.indexOf(this.state.fog) + 1) % cyc.length]; }
      else if (k === 'g') this.state.fogStep += 1;
      else if (k === 'b') this.state.wild = !this.state.wild;
      else if (k === 'o') this.state.shape = !this.state.shape;
      else if (k === 'v') { this.walkAmongThem('Jo', 2, 0); return; }
      else if (k === '0') { this.looked = false; this.fit(); return; }
      else if (k === 'x') { const cur = this.state.weather?.split(':')[0]; const i = cur ? WEATHER_KINDS.indexOf(cur as Weather['kind']) : -1; this.state.weather = i + 1 < WEATHER_KINDS.length ? WEATHER_KINDS[i + 1] : undefined; }
      else return;
      this.render();
    });
    // zoom with the wheel or a pinch toward the pointer, drag to pan; 0 fits the board again
    const cam = this.cameras.main;
    this.input.on('wheel', (p: Phaser.Input.Pointer) => {
      const e = p.event as WheelEvent;
      e.preventDefault();
      const zoom = Math.min(8, Math.max(0.25, cam.zoom * wheelZoomFactor(e)));
      const wx = cam.scrollX + p.x / cam.zoom, wy = cam.scrollY + p.y / cam.zoom;
      cam.setZoom(zoom).setScroll(wx - p.x / zoom, wy - p.y / zoom);
      this.looked = true;
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      // docs/186 EA12: the square under the mouse sees through the pieces in front of it
      if (!p.isDown) { this.seeHover = this.board && !p.wasTouch ? { x: Math.floor(p.worldX / this.cellPx), y: Math.floor(p.worldY / this.cellPx) } : undefined; return; }
      cam.setScroll(cam.scrollX - (p.x - p.prevPosition.x) / cam.zoom, cam.scrollY - (p.y - p.prevPosition.y) / cam.zoom);
      this.looked = true;
    });
    this.input.on(Phaser.Input.Events.GAME_OUT, () => { this.seeHover = undefined; });
    this.held = this.input.keyboard?.addKeys('W,A,S,D,UP,LEFT,DOWN,RIGHT,Q,E', false) as Record<string, Phaser.Input.Keyboard.Key> | undefined;
    this.caption.setResolution(dpr); this.legend.setResolution(dpr);
    this.scale.on(Phaser.Scale.Events.RESIZE, () => { ui.setSize(this.scale.width, this.scale.height); this.caption.setY(this.scale.height / dpr - 22); this.fit(); });
    this.render();
    // Test hooks
    const w = window as unknown as Record<string, unknown>;
    w['__fkGallery'] = () => ({ state: this.state, tiled: this.tiles.tiled, board: this.board });
    w['__fkGallerySet'] = (patch: Partial<GalleryState>) => { Object.assign(this.state, patch); this.render(); };
    w['__fkLighting'] = () => this.lighting.info();
    w['__fkStrike'] = () => this.lighting.strike();
    w['__fkLeap'] = () => this.lighting.leap();
    w['__fkFog'] = () => this.fogLayer.info();
    w['__fkTerrain'] = () => this.tiles.art?.info();
    w['__fkFogStep'] = () => { this.state.fogStep += 1; this.render(); return this.fogLayer.info(); };
    w['__fkFogFocus'] = (name?: string) => { this.state.focus = name; this.applyFocus(); };
    w['__fkWild'] = () => this.wild.info();
    w['__fkWildRows'] = () => this.preview?.rows ?? [];
    w['__fkWildWalk'] = (name: string, dx: number, dy: number) => this.walkAmongThem(name, dx, dy);
    // review: `[{ species, seed, legs: [{ at (ms from now), ms, x, y, z (pips), gait }] }]`, or nothing to go back to the room
    w['__fkWildScript'] = (rows?: Array<Omit<WildRow, 'id' | 'leaving'>>) => {
      const t = deviceNow();
      this.scriptAt = t;
      this.script = rows?.map((r, i) => ({ ...r, id: 1_000_000 + i, leaving: false, legs: r.legs.map((l) => ({ ...l, at: t + l.at })) }));
    };
    w['__fkWildHold'] = (ms?: number) => { this.heldAt = ms; };
    w['__fkUnitAt'] = (name: string) => this.sprites.find((u) => u.unit.name === name)?.tile;
    // docs/186 EA12: stand a unit on a square (review), and the pieces faded now
    w['__fkUnitPlace'] = (name: string, x: number, y: number) => { const s = this.sprites.find((u) => u.unit.name === name); if (!s) return false; s.tile = { x, y }; s.placeAt({ x, y }, this.cellPx); return true; };
    w['__fkSeeThrough'] = () => this.tiles.art?.seeThroughInfo();
    // docs/66: the fight stage on show (zones, entries, recipe) for a driver
    w['__fkFightStage'] = () => this.stage;
  }

  override update(time: number, delta: number): void {
    this.steer(delta);
    this.lighting.update(time); this.fogLayer.update(time); this.tiles.update(time);
    for (const u of this.sprites) if (u.depth !== u.y) u.setDepth(u.y);
    if (this.tiles.art?.painted) {
      const targets: SeeTarget[] = this.sprites.filter((u) => u.active && u.visible && u.alpha >= 0.05).map((u) => { const b = u.sprite.getBounds(); return unitTarget({ x: b.x, y: b.y, w: b.width, h: b.height }, u.depth); });
      if (this.seeHover) targets.push(squareTarget(this.seeHover, this.cellPx));
      this.tiles.art.seeThrough(targets, time);
    }
    const now = this.heldAt !== undefined ? this.scriptAt + this.heldAt : deviceNow();
    this.preview?.update(now);
    this.wild.update(this.script ?? this.preview?.rows ?? [], now, delta);
  }

  /** docs/186 EA13–EA14: the `look` and `country` params (else a place stage's own) laid on the board, as `session.ts` lays the stage's. */
  private stageLook(board: Snapshot['board'], stage?: Record<string, unknown>): void {
    const look = decodeLook(this.state.look ?? (stage?.['look'] as string | undefined), board.w, board.h);
    if (look) board.look = look; else delete board.look;
    const country = this.state.country ?? (stage?.['country'] as string | undefined);
    if (country) board.country = country; else delete board.country;
  }

  private get cellPx(): number { return (this.registry.get('boot') as GameBoot).manifest.cell; }

  private bodies(): PreviewBody[] { return this.sprites.map((s) => ({ unit: s.unit.unitId, x: s.tile.x, y: s.tile.y, size: 1, hero: s.unit.faction === 'hero' })); }

  /** Review: a unit walks `dx, dy` squares; the creatures near it react as to a resolved pulse. */
  private walkAmongThem(name: string, dx: number, dy: number): boolean {
    const s = this.sprites.find((u) => u.unit.name === name);
    if (!s || !this.board) return false;
    const to = { x: Math.max(0, Math.min(this.board.w - 1, s.tile.x + dx)), y: Math.max(0, Math.min(this.board.h - 1, s.tile.y + dy)) };
    const cell = (this.registry.get('boot') as GameBoot).manifest.cell;
    this.tweens.add({ targets: s, x: to.x * cell + cell / 2, y: (to.y + 1) * cell, duration: 450 * Math.max(Math.abs(dx), Math.abs(dy)), onComplete: () => s.placeAt(to, cell) });
    s.tile = { ...to };
    this.preview?.stir(this.bodies(), deviceNow() - 600);
    return true;
  }

  private applyFocus(): void {
    const u = this.sprites.find((s) => s.unit.name === this.state.focus);
    this.fogLayer.setFocus(u?.unit.unitId);
  }

  private hour(): number { return this.state.hour ?? encounterHour({ seed: this.state.seed }); }

  private weather(kit: string): Weather { return encounterWeather({ weather: this.state.weather, seed: this.state.seed }, kit, climateFor(kit).setting); }

  /** A party of four and a goblin band on open ground, spread across the board (legibility review). */
  private placeUnits(board: Snapshot['board']): void {
    this.sprites = [];
    if (!this.state.units && !this.state.fog) return;
    const boot = this.registry.get('boot') as GameBoot;
    const open: Array<{ x: number; y: number }> = [];
    for (let y = 1; y < board.h - 1; y++) for (let x = 1; x < board.w - 1; x++) if (board.tiles[y * board.w + x]?.terrain === 'Open') open.push({ x, y });
    if (open.length < 8) return;
    const step = this.state.fog ? this.state.fogStep : 0;
    const pick = (fx: number, fy: number) => {
      let best = 0, bd = Infinity;
      for (let i = 0; i < open.length; i++) { const d = Math.hypot(open[i]!.x - fx * board.w, open[i]!.y - fy * board.h); if (d < bd) { bd = d; best = i; } }
      return open.splice(best, 1)[0]!;
    };
    const cast: Array<[string, 'hero' | 'enemy', number, number, number]> = [
      ['Jo', 'hero', 1, 0.2, 0.45], ['Teo', 'hero', 2, 0.28, 0.65], ['Priya', 'hero', 4, 0.18, 0.75], ['Wren', 'hero', 5, 0.32, 0.35],
      ['Hobb', 'enemy', 257, 0.78, 0.5], ['Snagg', 'enemy', 253, 0.68, 0.3], ['Nib', 'enemy', 254, 0.7, 0.7],
    ];
    cast.forEach(([name, faction, appearance, fx, fy], i) => {
      const tile = pick(faction === 'hero' ? fx + step / board.w : fx, fy);
      const u: UnitState = { unitId: i + 1, name, faction, tile, hp: 10, hpMax: 10, guard: 0, initiative: 0, sprite: faction === 'hero' ? 'hero_fighter' : 'goblin', appearance, ...(name === 'Hobb' ? { leader: true } : {}), ...(faction === 'hero' ? { heroId: i + 1 } : {}) } as UnitState;
      const s = new UnitSprite(this, u, boot.manifest);
      if (faction === 'enemy') s.setFacing(-1);
      this.unitLayer.add(s);
      this.sprites.push(s);
    });
  }

  /** docs/66: the next setup, recipe, ground or party size. */
  /** Held keys: WASD or the arrows pan (screen px a second, whatever the zoom), Q / E zoom about the centre. */
  private steer(delta: number): void {
    const k = this.held;
    if (!k) return;
    const cam = this.cameras.main, dt = delta / 1000, down = (...n: string[]): boolean => n.some((x) => k[x]?.isDown);
    const dx = (down('D', 'RIGHT') ? 1 : 0) - (down('A', 'LEFT') ? 1 : 0), dy = (down('S', 'DOWN') ? 1 : 0) - (down('W', 'UP') ? 1 : 0);
    const dz = (down('E') ? 1 : 0) - (down('Q') ? 1 : 0);
    if (!dx && !dy && !dz) return;
    if (dx || dy) cam.setScroll(cam.scrollX + (dx * 900 * dt) / cam.zoom, cam.scrollY + (dy * 900 * dt) / cam.zoom);
    if (dz) {
      const zoom = Math.min(8, Math.max(0.25, cam.zoom * Math.pow(ZOOM_KEY_RATE, dz * dt)));
      const cx = this.scale.width / 2, cy = this.scale.height / 2;
      const wx = cam.scrollX + cx / cam.zoom, wy = cam.scrollY + cy / cam.zoom;
      cam.setZoom(zoom).setScroll(wx - cx / zoom, wy - cy / zoom);
    }
    this.looked = true;
  }

  private cycleFight(k: string): void {
    const s = this.state;
    const next = <T,>(list: readonly T[], cur: T): T => list[(list.indexOf(cur) + 1) % list.length]!;
    if (k === 'c') s.fight = next(SETUPS as readonly string[], s.fight ?? 'Clash');
    if (k === 'g') { s.ground = next(GROUNDS as readonly string[], s.ground); s.recipe = undefined; }
    if (k === 'h') s.heroes = next(PARTIES as readonly number[], s.heroes);
    if (k === 'r') s.recipe = next(['', ...this.recipes()], s.recipe ?? '') || undefined;
  }

  private recipes(): string[] {
    return recipesFor(wasmModule().fight_recipes() as Array<[string, string[]]>, this.state.ground);
  }

  /** The picker over the canvas: setup, recipe (for the ground), ground, party, seed, zones. */
  private drawPicker(): void {
    const s = this.state;
    if (!this.picker) {
      const p = document.createElement('div');
      p.setAttribute('data-testid', 'fight-picker');
      p.style.cssText = 'position:fixed;top:8px;right:8px;z-index:10;display:flex;gap:6px;align-items:center;flex-wrap:wrap;max-width:60vw;padding:6px 8px;background:#0b1020e6;border:1px solid #e0b24a;color:#e8e2d0;font:12px/1.2 "Alegreya Sans",sans-serif';
      document.body.appendChild(p);
      this.picker = p;
    }
    const p = this.picker;
    p.textContent = '';
    const select = (label: string, id: string, values: ReadonlyArray<string>, cur: string, set: (v: string) => void, names?: (v: string) => string) => {
      const l = document.createElement('label');
      l.textContent = `${label} `;
      const el = document.createElement('select');
      el.setAttribute('data-testid', `pick-${id}`);
      for (const v of values) { const o = document.createElement('option'); o.value = v; o.textContent = names ? names(v) : v; if (v === cur) o.selected = true; el.appendChild(o); }
      el.onchange = () => { set(el.value); this.render(); };
      l.appendChild(el);
      p.appendChild(l);
    };
    select('Setup', 'setup', SETUPS, s.fight ?? 'Clash', (v) => { s.fight = v; });
    select('Ground', 'ground', GROUNDS, s.ground, (v) => { s.ground = v; s.recipe = undefined; });
    select('Recipe', 'recipe', ['', ...this.recipes()], s.recipe ?? '', (v) => { s.recipe = v || undefined; }, (v) => v || 'any (as a fight draws it)');
    select('Heroes', 'heroes', PARTIES.map(String), String(s.heroes), (v) => { s.heroes = Number(v); });
    const seed = document.createElement('input');
    seed.type = 'number'; seed.value = String(s.seed); seed.style.width = '5em';
    seed.setAttribute('data-testid', 'pick-seed');
    seed.onchange = () => { s.seed = Number(seed.value) || 0; this.render(); };
    const sl = document.createElement('label'); sl.textContent = 'Seed '; sl.appendChild(seed); p.appendChild(sl);
    const zl = document.createElement('label');
    const z = document.createElement('input'); z.type = 'checkbox'; z.checked = s.zones; z.onchange = () => { s.zones = z.checked; this.render(); };
    zl.appendChild(z); zl.append(' zones'); p.appendChild(zl);
  }

  /** docs/66: the plan over the board — zones, spots, the leader, entries named as the log names them. */
  private drawPlan(st: FightStage, cell: number): void {
    this.plan?.destroy(); this.plan = undefined;
    for (const t of this.planLabels) t.destroy();
    this.planLabels = [];
    if (!this.state.zones) return;
    const g = this.add.graphics();
    this.plan = g;
    this.propsLayer.add(g);
    const zone = (ts: readonly { x: number; y: number }[], colour: number, fill = 0.22) => {
      for (const t of ts) {
        g.fillStyle(colour, fill); g.fillRect(t.x * cell + 1, t.y * cell + 1, cell - 2, cell - 2);
        g.lineStyle(1, colour, 0.85); g.strokeRect(t.x * cell + 1.5, t.y * cell + 1.5, cell - 3, cell - 3);
      }
    };
    const label = (t: { x: number; y: number }, text: string, colour: string) => {
      const o = this.add.text((t.x + 0.5) * cell, t.y * cell - 1, text, { fontFamily: '"Alegreya Sans", sans-serif', fontStyle: '700', fontSize: `${Math.round(cell * 0.4)}px`, color: colour, stroke: '#1a1006', strokeThickness: 2 }).setOrigin(0.5, 1).setResolution(4);
      this.propsLayer.add(o);
      this.planLabels.push(o);
    };
    zone(st.zones.heroes, 0x4aa3ff, 0.16);
    st.zones.foes.forEach((f, i) => { zone(f, 0xe8574a, 0.2); const c = f[Math.floor(f.length / 2)]; if (c) label(c, `FOES ${i + 1}`, '#ffb3a6'); });
    zone(st.zones.ward, 0x5be38a, 0.35);
    zone(st.zones.landmark, 0xffd166, 0.35);
    zone(st.zones.thing, 0x5ee6f0, 0.35);
    for (const e of st.entries) { zone(e.tiles, 0xff9a3c, 0.4); const t = e.tiles[0]; if (t) label(t, `from ${e.from}`, '#ffc48a'); }
    // the heroes' spots, the front first
    st.heroSpots.slice(0, this.state.heroes).forEach((t) => { g.lineStyle(2, 0x4aa3ff, 1); g.strokeCircle((t.x + 0.5) * cell, (t.y + 0.5) * cell, cell * 0.3); });
    g.lineStyle(2, 0xffd166, 1); g.strokeCircle((st.leader.x + 0.5) * cell, (st.leader.y + 0.5) * cell, cell * 0.42);
    const h = st.zones.heroes[0];
    if (h) label(h, 'HEROES', '#9fd0ff');
  }

  /** docs/66: a staged fight in place of a layout. */
  private renderFight(): void {
    const s = this.state;
    const boot = this.registry.get('boot') as GameBoot;
    // docs/187 PK2: `place=<kit>` stages the fight in a place, with its kit, country and look (docs/186 EA13–EA14)
    const raw = (s.place
      ? wasmModule().stage_place(s.fight ?? 'Clash', s.place, s.ground, s.heroes, 0, BigInt(s.seed))
      : wasmModule().stage_fight(s.fight ?? 'Clash', s.recipe ?? '', s.ground, s.heroes, 0, BigInt(s.seed))) as Record<string, unknown>;
    const st = fightStageFromWasm(raw);
    this.stage = st;
    const board = st.board;
    board.kit = s.kit ?? (s.place ? String(raw['kit'] || s.place) : board.kit);
    board.seed = s.seed;
    this.stageLook(board, s.place ? raw : undefined);
    this.board = board;
    this.tiles.draw({ board }, s.marks);
    this.edge.draw(board);
    for (const o of this.sprites) o.destroy();
    this.sprites = [];
    this.wild.setMode(false, false);
    this.preview?.free(); this.preview = undefined;
    const put = (u: UnitState) => { const sp = new UnitSprite(this, u, boot.manifest); this.unitLayer.add(sp); this.sprites.push(sp); return sp; };
    const heroes = ['Jo', 'Teo', 'Priya', 'Wren'];
    st.heroSpots.slice(0, s.heroes).forEach((t, i) => put({ unitId: i + 1, heroId: i + 1, name: heroes[i % 4]!, faction: 'hero', tile: t, hp: 10, hpMax: 10, guard: 0, initiative: 0, sprite: 'hero_fighter', appearance: [1, 2, 4, 5][i % 4] } as UnitState));
    put({ unitId: 90, name: 'Grukk', faction: 'enemy', tile: st.leader, hp: 20, hpMax: 20, guard: 0, initiative: 0, sprite: 'goblin', appearance: 257, leader: true } as UnitState).setFacing(-1);
    this.drawPlan(st, boot.manifest.cell);
    const hour = this.hour();
    this.lighting.setMode(s.light, false);
    // docs/186 EA14: an outdoor place is lit and weathered as its country
    const lightKit = oryxKeyOf(board) ?? 'forest';
    const weather = this.weather(lightKit);
    this.lighting.setArt(this.tiles.painted);
    this.lighting.setBoard(board, this.tiles.tiled, lightKit, hour, weather, s.season);
    this.lighting.setUnits(this.sprites.map((u) => ({ key: String(u.unit.unitId), obj: u, body: u.sprite, hero: u.unit.faction === 'hero', down: false })));
    this.fogLayer.set(board, undefined, []);
    this.tiles.art?.setFog(undefined, undefined);
    this.legend.setText('plan: blue heroes (rings = spots, front first) · red foe groups (gold ring = leader) · green ward · gold landmark · cyan thing · ember entries · lit terraces = high ground');
    this.legend.setWordWrapWidth(Math.max(240, this.scale.width / pixelRatio() - 700));
    this.legend.setVisible(true);
    this.drawPicker();
    this.fit();
    const hz = st.zones.heroes[0];
    this.caption.setText(`${st.setup} · ${st.recipe} · ${s.ground} · ${s.heroes} hero${s.heroes === 1 ? '' : 'es'} · seed ${s.seed} · ${board.w}×${board.h} · heroes ${hz ? sideOf(hz, board.w, board.h) : '?'} · ${st.entries.length} entr${st.entries.length === 1 ? 'y' : 'ies'} · proved ${st.proof.toLowerCase()} · art ${this.tiles.artId || 'oryx'}   [C setup  R recipe  G ground  H heroes  N/P seed  M marks  wheel or Q/E zoom  drag or WASD pan  0 fit]`);
    this.registry.set('galleryReady', true);
  }

  /** docs/189 EO: every thing of the objects atlas, each variant, lamps lit and out, a few burning, on a kit's floor. */
  private renderThings(): void {
    const s = this.state;
    const boot = this.registry.get('boot') as GameBoot;
    const cell = boot.manifest.cell;
    const table = objectTable(this) ?? {};
    const kinds = Object.keys(table);
    const lamps = new Set(kinds.filter((k) => thingShape(k).lamp));
    const flammable = new Set(kinds.filter((k) => thingShape(k).flammable));
    const room = showroom(table, { lamps, flammable });
    const raw = wasmModule().generate_board('dungeon', BigInt(s.seed), 18, 12) as Record<string, unknown>;
    const sample = boardFromWasmObj(raw['board'] as Record<string, unknown>);
    const open = sample.tiles.find((t) => t?.terrain === 'Open')!, wall = sample.tiles.find((t) => t?.terrain === 'Wall')!;
    const tiles: TileData[] = [];
    for (let y = 0; y < room.h; y++) for (let x = 0; x < room.w; x++) tiles.push({ ...(y === 0 || x === 0 || x === room.w - 1 || y === room.h - 1 ? wall : open) });
    const board: Snapshot['board'] = { ...sample, w: room.w, h: room.h, tiles, kit: s.kit ?? 'house', seed: s.seed };
    delete board.look; delete board.country;
    this.board = board;
    this.tiles.draw({ board }, false);
    this.edge.draw(board);
    for (const o of this.sprites) o.destroy();
    this.sprites = [];
    room.heroes.forEach((t, i) => { const u = { unitId: i + 1, heroId: i + 1, name: 'Jo', faction: 'hero', tile: t, hp: 10, hpMax: 10, guard: 0, initiative: 0, sprite: 'hero_fighter', appearance: [1, 2, 4, 5][i % 4] } as UnitState; const sp = new UnitSprite(this, u, boot.manifest); this.unitLayer.add(sp); this.sprites.push(sp); });
    this.thingFx ??= new MatterLayer(this, this.matterLayers, cell, { things: { units: this.unitLayer, light: (x, y) => this.lighting.lightAt(x, y) } });
    this.thingFx.set([], room.things);
    for (const t of this.thingLabels) t.destroy();
    this.thingLabels = room.labels.map((l) => {
      const o = this.add.text(l.tile.x * cell + 1, l.tile.y * cell + 2, l.text, { fontFamily: 'monospace', fontSize: `${Math.round(cell * 0.3)}px`, color: '#f4ead2', stroke: '#0b1020', strokeThickness: 2 }).setResolution(4);
      this.propsLayer.add(o);
      return o;
    });
    const hour = this.hour();
    this.lighting.setMode(s.light, false);
    const lightKit = oryxKeyOf(board) ?? board.kit ?? 'house';
    const weather = this.weather(lightKit);
    this.lighting.setArt(this.tiles.painted);
    this.lighting.setBoard(board, this.tiles.tiled, lightKit, hour, weather, s.season);
    this.lighting.setUnits(this.sprites.map((u) => ({ key: String(u.unit.unitId), obj: u, body: u.sprite, hero: true, down: false })));
    this.fogLayer.set(board, undefined, []);
    this.tiles.art?.setFog(undefined, undefined);
    this.legend.setVisible(false);
    this.fit();
    this.caption.setText(`things · ${room.things.length} on show · ${kinds.length} kinds · kit ${board.kit} · ${formatHour(hour)} · art ${this.tiles.artId || 'oryx'}   [T/Y hour  L light  wheel or Q/E zoom  drag or WASD pan  0 fit]`);
    (window as unknown as Record<string, unknown>)['__fkThings'] = () => ({ things: room.things.length, kinds: kinds.length, drawn: this.thingFx?.drawnCount() });
    this.registry.set('galleryReady', true);
  }

  private render(): void {
    if (this.state.things) { this.renderThings(); return; }
    if (this.state.fight) { this.renderFight(); return; }
    this.stage = undefined;
    this.plan?.destroy(); this.plan = undefined;
    for (const t of this.planLabels) t.destroy();
    this.planLabels = [];
    this.picker?.remove(); this.picker = undefined;
    const wasm = wasmModule();
    // docs/38: `O` (or `shape=1`) cuts the layout to a seeded free-form field, as fights are
    const make = this.state.shape ? wasm.generate_field : wasm.generate_board;
    const raw = make(this.state.style, BigInt(this.state.seed), this.state.w, this.state.h) as Record<string, unknown>;
    const board = boardFromWasmObj(raw['board'] as Record<string, unknown>);
    board.kit = this.state.kit ?? String(raw['kit']);
    board.seed = this.state.seed;
    this.stageLook(board);
    this.board = board;
    this.tiles.draw({ board }, this.state.marks);
    this.edge.draw(board);
    const old = this.sprites;
    const wasSeen = new Set(this.fogView ? old.filter((s) => s.unit.faction === 'enemy').map((s) => s.unit.unitId) : []);
    this.placeUnits(board);
    this.fogView = undefined;
    const veils = parseFogVeils(this.state.fog);
    if (veils && this.sprites.length) {
      // GALLERY-ONLY: a mock fog over the party; foes out of its sight leave the board (the server strips them)
      const heroes = this.sprites.filter((s) => s.unit.faction === 'hero');
      const { fog, seen } = mockFog(board, veils, heroes.map((s) => ({ unit: s.unit.unitId, tile: s.unit.tile, darkvision: s.unit.name === 'Wren', keen: s.unit.name === 'Priya' })),
        this.sprites.filter((s) => s.unit.faction === 'enemy').map((s) => ({ unit: s.unit.unitId, tile: s.unit.tile, look: s.unit.appearance ?? 0 })), this.state.seed);
      this.fogView = fog;
      this.sprites = this.sprites.filter((s) => { if (s.unit.faction === 'enemy' && !seen.has(s.unit.unitId)) { s.destroy(); return false; } return true; });
      for (const s of this.sprites) if (s.unit.faction === 'enemy' && old.length && !wasSeen.has(s.unit.unitId)) this.fogLayer.enter(s);
    }
    const nowSeen = new Set(this.sprites.map((s) => s.unit.unitId));
    for (const s of old) { if (this.fogView && s.unit.faction === 'enemy' && wasSeen.has(s.unit.unitId) && !nowSeen.has(s.unit.unitId)) this.fogLayer.leave(s); else s.destroy(); }
    const hour = this.hour();
    this.lighting.setMode(this.state.light, false);
    const lightKit = oryxKeyOf(board) ?? board.kit;
    const weather = this.weather(lightKit);
    this.lighting.setArt(this.tiles.painted);
    this.lighting.setBoard(board, this.tiles.tiled, lightKit, hour, weather, this.state.season);
    this.lighting.setUnits(this.sprites.map((s) => ({ key: String(s.unit.unitId), obj: s, body: s.sprite, hero: s.unit.faction === 'hero', down: false })));
    this.fogLayer.setMode({ particles: this.state.light === 'full', still: false, wind: weather.wind, weather: weather.kind, strength: this.state.light === 'soft' ? 0.85 : 1 });
    this.fogLayer.set(board, this.fogView, this.sprites.filter((s) => s.unit.faction === 'hero').map((s, i) => ({ unit: s.unit.unitId, colour: PLAYER_COLOURS[i % PLAYER_COLOURS.length]!, tile: s.unit.tile })));
    this.tiles.art?.setFog(this.fogView, this.fogLayer.veil);
    this.applyFocus();
    // docs/35: a fresh room of creatures for this board, hour and sky (warmed up so some are about)
    this.preview?.free(); this.preview = undefined;
    this.wild.setMode(this.state.wild, false);
    this.wild.setBoard(board, this.tiles.tiled, board.kit, this.tiles.painted ? this.tiles.art?.occluders() : undefined);
    if (this.state.wild) this.preview = new WildPreviewRoom(habitatOf(board, this.bodies()), roomOf(board.kit, hour, weather, this.state.season), this.state.seed, deviceNow());
    this.legend.setVisible(this.state.marks);
    this.fit();
    const kitName = KITS[board.kit]?.name ?? board.kit;
    const day = this.lighting.dayLight;
    this.caption.setText(`${this.state.style} · seed ${this.state.seed} · kit ${board.kit} (${kitName}) · ${board.w}×${board.h} · ${formatHour(hour)} ${day?.phase ?? ''} ${day?.setting ?? ''} · ${weatherLabel(weather)}${this.state.season ? ` · ${seasonLabel(this.state.season)}` : ''}${this.state.fog ? ` · fog ${this.state.fog} (mock)` : ''}${this.state.wild ? ' · wildlife' : ''}${this.state.shape ? ' · shaped' : ''} · light ${this.state.light} · art ${this.tiles.artId || 'oryx'}   [N/P seed  C style  K kit  M marks  T/Y hour  X weather  Z season  F fog  G step  L light  U units  B wildlife  O shape  V walk  wheel or Q/E zoom  drag or WASD pan  0 fit]`);
    this.registry.set('galleryReady', true);
  }

  private fit(): void {
    if (!this.board || this.looked) return;
    const boot = this.registry.get('boot') as GameBoot;
    const geom = { w: this.board.w, h: this.board.h, cell: boot.manifest.cell, top: this.tiles.headroom };
    // docs/66: a fight's field is shown whole, at whatever zoom fits it (they run to 44 × 30)
    const top = this.state.fight ? 64 : 0;
    const free = Math.floor(Math.min((this.scale.width - 32) / (geom.w * geom.cell), (this.scale.height - 30 - top - 16) / (geom.h * geom.cell + geom.top)) * 4) / 4;
    const zoom = this.state.zoom ?? (this.state.fight ? Math.max(0.5, Math.min(4, free)) : fitZoom(geom, this.scale.width, this.scale.height - 30));
    centreBoard(this.cameras.main, geom, zoom, { x: 0, y: top, w: this.scale.width, h: this.scale.height - 30 - top });
  }
}
